import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Trash,
  Search,
  MoreVertical,
  ListTodo,
  FilePlus,
  Save,
  Pencil,
  User,
  Clock,
  HelpCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Dropdown, Modal, MenuProps, Tooltip } from "antd";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import {
  DropdownService,
  SchemaService,
  DynamicModuleService,
} from "@/services/APIs";
import PtsProjectService from "@/services/APIs/PtsProjectService";
import PTSNavigationRoutes from "../../utils/navigationRoutes";
import { get } from "@/services/axiosUitils";

const PROJECT_SCHEMA_NAME = "PTS Project";
const PROJECT_TABLE_API = "pts_project";
const PROJECT_MEMBER_SCHEMA_NAME = "PTS Project Members Child";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const TASK_SCHEMA_NAME = "PTS Task";
const TASK_CATEGORY_SCHEMA_NAME = "PTS Task Category";

// Component to fetch and display employee name in tooltip (who last edited)
const EmployeeTooltipContent = ({
  userId,
}: {
  userId: string | null | undefined;
}) => {
  const { data: employeeData, isLoading } = useQuery({
    queryKey: ["employee-by-user-id", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      try {
        const modelName = encodeURIComponent(EMPLOYEE_SCHEMA_NAME);
        const response = await get<any>(
          `/formily/dynamic-model/${modelName}/`,
          { params: { users__eq: userId } }
        );
        return response;
      } catch (error) {
        console.error("Error fetching employee data:", error);
        return null;
      }
    },
    enabled: !!userId,
  });

  const employeeName = useMemo(() => {
    if (!employeeData) {
      return "Loading...";
    }
    const results =
      employeeData?.results ??
      employeeData?.data ??
      (Array.isArray(employeeData) ? employeeData : []);
    if (results.length === 0) {
      return "Unknown Employee";
    }
    const employee = results[0];
    const firstName =
      employee.first_name ??
      employee.firstname ??
      employee.fname ??
      employee.firstName ??
      "";
    const lastName =
      employee.last_name ??
      employee.lastname ??
      employee.lname ??
      employee.lastName ??
      "";
    return `${firstName} ${lastName}`.trim() || "Unknown Employee";
  }, [employeeData]);

  if (isLoading) {
    return <span className="text-gray-700">Loading...</span>;
  }
  return <span className="text-gray-700">{employeeName}</span>;
};

const ProjectPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [projectType, setProjectType] = useState<string>("all");
  const [viewTasksModalOpen, setViewTasksModalOpen] = useState(false);
  const [viewTasksProjectId, setViewTasksProjectId] = useState<string | null>(
    null
  );
  const [viewTasksProjectName, setViewTasksProjectName] = useState<string>("");
  const [saveAsTemplateDialogOpen, setSaveAsTemplateDialogOpen] =
    useState(false);
  const [saveAsTemplateProjectId, setSaveAsTemplateProjectId] = useState<
    string | null
  >(null);
  const [saveAsTemplateConfirmLoading, setSaveAsTemplateConfirmLoading] =
    useState(false);

  // Toggle project active status via dropdown API
  const updateActiveMutation = useMutation({
    mutationFn: (params: { pk: string; is_active: boolean }) =>
      DropdownService.manageActiveRecord({
        table: PROJECT_TABLE_API,
        pk: params.pk,
        is_active: params.is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["moduleData", PROJECT_SCHEMA_NAME],
      });
      notify.success("Project status updated");
    },
    onError: (error: any) => {
      notify.error(error?.message ?? "Failed to update active status");
    },
  });

  // Fetch employee dropdown for manager name
  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Fetch employee data to get first_name and last_name
  const { data: employeeDataResponse } = useQuery({
    queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => {
      return DynamicModuleService.getGridColItems(
        EMPLOYEE_SCHEMA_NAME,
        1,
        1000,
        {}
      );
    },
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Extract employee records
  const employeeRecords = useMemo(() => {
    if (!employeeDataResponse) {
      return [];
    }
    if (Array.isArray(employeeDataResponse)) {
      return employeeDataResponse;
    }
    if (
      employeeDataResponse.results &&
      Array.isArray(employeeDataResponse.results)
    ) {
      return employeeDataResponse.results;
    }
    return [];
  }, [employeeDataResponse]);

  // Get employee column grid data to find first_name and last_name fields
  const { data: employeeColumnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_SCHEMA_NAME),
  });

  // Find employee field names
  const findEmployeeFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!employeeColumnGridData?.grid) {
        return null;
      }
      for (const field of employeeColumnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(term => {
            const lowerTerm = term.toLowerCase();
            return (
              fieldName.includes(lowerTerm) ?? fieldTitle.includes(lowerTerm)
            );
          })
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [employeeColumnGridData]
  );

  const employeeFirstNameField = findEmployeeFieldName([
    "first_name",
    "firstname",
    "fname",
  ]);
  const employeeLastNameField = findEmployeeFieldName([
    "last_name",
    "lastname",
    "lname",
  ]);

  // Fetch all project members to count assigned members per project
  const { data: projectMembersData } = useQuery({
    queryKey: ["project-members", PROJECT_MEMBER_SCHEMA_NAME],
    queryFn: async () => {
      // Fetch all project members with pagination
      let allMembers: any[] = [];
      let page = 1;
      const pageSize = 200;
      let hasMore = true;

      while (hasMore) {
        try {
          const response = await DynamicModuleService.getGridColItems(
            PROJECT_MEMBER_SCHEMA_NAME,
            page,
            pageSize
          );
          // Handle response structure - could be array or object with results
          let members: any[] = [];
          let next: string | null = null;

          if (Array.isArray(response)) {
            members = response;
            hasMore = false; // If it's an array, assume no pagination
          } else if (response?.results && Array.isArray(response.results)) {
            members = response.results;
            next = response.next ?? null;
          }

          if (members.length > 0) {
            allMembers = [...allMembers, ...members];
            hasMore = next !== null && next !== undefined;
            page++;
          } else {
            hasMore = false;
          }
        } catch (error) {
          console.error("Error fetching project members:", error);
          hasMore = false;
        }
      }
      return allMembers;
    },
    enabled: !!PROJECT_MEMBER_SCHEMA_NAME,
  });

  // Calculate member counts per project
  useEffect(() => {
    if (projectMembersData) {
      const counts: Record<string, number> = {};
      projectMembersData.forEach((member: any) => {
        const projectId =
          member.project_id ?? member.project ?? member.projectId;
        if (projectId) {
          counts[String(projectId)] = (counts[String(projectId)] ?? 0) + 1;
        }
      });
    }
  }, [projectMembersData]);

  // Get column grid data to generate all columns dynamically
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", PROJECT_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(PROJECT_SCHEMA_NAME),
  });

  // Get employee first and last name by ID
  const getEmployeeNamesById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return { firstName: "-", lastName: "" };
      }

      const employee = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(id)
      );

      if (!employee) {
        // Fallback to dropdown label if employee record not found
        const dropdownEmployee = employeeOptions?.find(
          (item: { value: string; label: string }) =>
            String(item.value) === String(id)
        );
        if (dropdownEmployee?.label) {
          const nameParts = dropdownEmployee.label.split(" ");
          return {
            firstName: nameParts[0] ?? "-",
            lastName: nameParts.slice(1).join(" ") ?? "",
          };
        }
        return { firstName: "-", lastName: "" };
      }

      const firstName =
        (employeeFirstNameField && employee[employeeFirstNameField]) ??
        employee.first_name ??
        employee.firstname ??
        employee.fname ??
        "";
      const lastName =
        (employeeLastNameField && employee[employeeLastNameField]) ??
        employee.last_name ??
        employee.lastname ??
        employee.lname ??
        "";

      return {
        firstName: firstName ?? "-",
        lastName: lastName ?? "",
      };
    },
    [
      employeeRecords,
      employeeOptions,
      employeeFirstNameField,
      employeeLastNameField,
    ]
  );

  // Helper function to format date from ISO string to DD-MM-YYYY
  const formatDate = useCallback(
    (dateString: string | null | undefined): string => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return "-";
        }
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch {
        return "-";
      }
    },
    []
  );

  // Format date and time for "Edited" tooltip (e.g. "Dec 28, 10:30 AM")
  const formatDateTime = useCallback(
    (dateString: string | null | undefined): string => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return "-";
        }
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, "0");
        return `${month} ${day}, ${displayHours}:${displayMinutes} ${ampm}`;
      } catch {
        return String(dateString);
      }
    },
    []
  );

  // Generate all columns dynamically
  const columns: ProColumns<any>[] = useMemo(() => {
    const handleDelete = async (id: string | undefined) => {
      if (!id) {
        notify.error("Project ID is missing");
        return;
      }
      try {
        const response = await DynamicModuleService.deleteItem(
          PROJECT_SCHEMA_NAME,
          String(id)
        );
        if (response) {
          queryClient.invalidateQueries({
            queryKey: ["moduleData", PROJECT_SCHEMA_NAME],
          });
        }
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to delete project");
      }
    };

    const handleEdit = (id: string | undefined) => {
      if (!id) {
        notify.error("Project ID is missing");
        return;
      }
      navigate(PTSNavigationRoutes.EDIT_PROJECT(String(id)));
    };

    if (!columnGridData?.grid) {
      return [];
    }

    // Find field names from schema
    const findFieldName = (searchTerms: string[]) => {
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        // Try exact match first, then includes
        for (const term of searchTerms) {
          const lowerTerm = term.toLowerCase();
          if (
            fieldName === lowerTerm ||
            fieldName.includes(lowerTerm) ||
            fieldTitle === lowerTerm ||
            fieldTitle.includes(lowerTerm)
          ) {
            return field.field_name; // Return actual field name (not lowercase)
          }
        }
      }
      return null;
    };

    // Find all date fields from schema
    const findDateFields = () => {
      const dateFields: string[] = [];
      for (const field of columnGridData.grid) {
        const fieldType = field.field_type?.toLowerCase() ?? "";
        const fieldName = field.field_name.toLowerCase();
        // Check if field is a date type
        if (
          fieldType === "date" ||
          fieldType === "datetime" ||
          fieldType === "timestamp" ||
          fieldName.includes("date") ||
          fieldName.includes("created_at") ||
          fieldName.includes("updated_at") ||
          fieldName.includes("start_date") ||
          fieldName.includes("end_date")
        ) {
          dateFields.push(field.field_name);
        }
      }
      return dateFields;
    };

    const dateFields = findDateFields();

    const projectNameField = findFieldName([
      "projectname",
      "project_name",
      "name",
      "project",
    ]);
    const clientField = findFieldName(["clientname", "client", "client_name"]);
    const managerField = findFieldName([
      "project_manager",
      "manager",
      "manager_id",
    ]);
    const hoursLoggedField = findFieldName([
      "hours_logged",
      "total_hours",
      "hours",
      "logged_hours",
    ]);
    const actuallyHoursField = findFieldName(["actually_hours", "actualhours"]);
    const projectTypeField = findFieldName([
      "project_type1",
      "project_type",
      "type",
      "projecttype",
    ]);

    const varianceField = findFieldName([
      "variance",
      "hours_variance",
      "taskvariance",
    ]);

    // Only use fields that exist in the schema, otherwise use null to skip dataIndex

    // Build custom columns matching the image
    // Only set dataIndex if the field exists in schema to avoid "Column not found" error
    const customColumns: ProColumns<any>[] = [
      {
        title: "Project Name",
        dataIndex: projectNameField
          ? projectNameField.toLowerCase()
          : undefined,
        key: "project_name",
        width: 280,
        render: (_: any, record: any) => {
          let projectName =
            record.projectname ??
            record.project_name ??
            record.projectName ??
            "";

          if (!projectName && projectNameField) {
            projectName =
              record[projectNameField.toLowerCase()] ??
              record[projectNameField] ??
              "";
          }

          if (!projectName) {
            const possibleFields = ["name", "project"];
            for (const field of possibleFields) {
              if (record[field]) {
                projectName = record[field];
                break;
              }
            }
          }

          if (
            !projectName ||
            projectName === record?.id ||
            projectName === record?.ID ||
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
              String(projectName)
            )
          ) {
            projectName = "-";
          }

          const recordId =
            record?.id ?? record?.ID ?? record?.Id ?? record?._id;

          const createdBy = record.created_by;
          const updatedBy = record.updated_by;
          const createdOn = record.created_on;
          const updatedOn = record.updated_on;
          const isEdited = createdBy !== updatedBy || createdOn !== updatedOn;
          const formattedDate = isEdited ? formatDateTime(updatedOn) : "";

          return (
            <div className="flex flex-col gap-1">
              <span
                className="cursor-pointer font-medium text-blue-600 hover:text-blue-800 hover:underline"
                onClick={() => handleEdit(recordId)}
              >
                {projectName}
              </span>
              {isEdited && (
                <Tooltip
                  title={
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-800" />
                        <EmployeeTooltipContent userId={updatedBy} />
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-gray-800" />
                        <span className="text-gray-700">{formattedDate}</span>
                      </div>
                    </div>
                  }
                  placement="bottom"
                  overlayInnerStyle={{
                    backgroundColor: "#ffffff",
                    color: "#374151",
                    borderRadius: "8px",
                    border: "1px solid #e0e6eb",
                    padding: "8px 12px",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  <div
                    className="flex cursor-pointer items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                    onClick={e => {
                      e.stopPropagation();
                      handleEdit(recordId);
                    }}
                  >
                    <Pencil size={12} />
                    <span>Edited</span>
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Project Type",
        dataIndex: projectTypeField
          ? projectTypeField.toLowerCase()
          : undefined,
        key: "project_type",
        render: (_: any, record: any) => {
          const projectType =
            (projectTypeField &&
              (record[projectTypeField.toLowerCase()] ??
                record[projectTypeField])) ??
            record.project_type1 ??
            record.project_type ??
            record.type ??
            "-";
          return projectType;
        },
      },
      {
        title: "Client Name",
        dataIndex: clientField ? clientField.toLowerCase() : undefined,
        key: "client",
        render: (_: any, record: any) => {
          return (
            (clientField &&
              (record[clientField.toLowerCase()] ?? record[clientField])) ??
            record.clientname ??
            record.client ??
            record.client_name ??
            "-"
          );
        },
      },
      {
        title: "Manager",
        dataIndex: managerField ? managerField.toLowerCase() : undefined,
        key: "manager",
        render: (_: any, record: any) => {
          const managerId =
            (managerField &&
              (record[managerField.toLowerCase()] ?? record[managerField])) ??
            record.project_manager ??
            record.manager_id ??
            record.manager;
          if (!managerId) {
            return "-";
          }
          const employeeNames = getEmployeeNamesById(managerId);
          return (
            `${employeeNames.firstName} ${employeeNames.lastName}`.trim() ||
            managerId
          );
        },
      },
      {
        title: "Hours Logged",
        dataIndex: hoursLoggedField
          ? hoursLoggedField.toLowerCase()
          : undefined,
        key: "hours_logged",
        render: (_: any, record: any) => {
          const hours =
            (hoursLoggedField &&
              (record[hoursLoggedField.toLowerCase()] ??
                record[hoursLoggedField])) ??
            record.hours_logged ??
            record.total_hours ??
            record.hours ??
            0;
          return typeof hours === "number" ? `${hours}h` : (hours ?? "0h");
        },
      },
      {
        title: "Actually Hours",
        dataIndex: actuallyHoursField
          ? actuallyHoursField.toLowerCase()
          : undefined,
        key: "actually_hours",
        render: (_: any, record: any) => {
          const hours =
            (actuallyHoursField &&
              (record[actuallyHoursField.toLowerCase()] ??
                record[actuallyHoursField])) ??
            record.actually_hours ??
            record.actual_hours ??
            record.actualhours ??
            0;
          return typeof hours === "number" ? `${hours}h` : (hours ?? "0h");
        },
      },
      {
        title: "Variance",
        dataIndex: varianceField ?? undefined,
        key: "variance",
        render: (_: any, record: any) => {
          const variance =
            (varianceField && record[varianceField]) ??
            record.variance ??
            record.hours_variance ??
            0;

          // Convert to number for comparison
          const varianceNum =
            typeof variance === "number"
              ? variance
              : typeof variance === "string"
                ? parseFloat(variance)
                : 0;

          // Handle null, undefined, or NaN
          if (
            varianceNum === null ||
            varianceNum === undefined ||
            isNaN(varianceNum)
          ) {
            return <span className="text-gray-900">-</span>;
          }

          // Format the display value
          const displayValue =
            typeof variance === "number"
              ? variance
              : typeof variance === "string"
                ? variance
                : String(varianceNum);

          // Apply color based on value
          if (varianceNum < 0) {
            // Negative - red text
            return (
              <span className="font-normal text-red-600">{displayValue}</span>
            );
          } else if (varianceNum > 0) {
            // Positive - green text
            return (
              <span className="font-normal text-green-600">{displayValue}</span>
            );
          } else {
            // Zero - normal text
            return <span className="text-gray-900">{displayValue}</span>;
          }
        },
      },
    ];

    // Add columns for all date fields with DD-MM-YYYY formatting
    const dateColumns: ProColumns<any>[] = dateFields
      .filter(dateField => {
        // Exclude date fields that are already in customColumns
        const fieldLower = dateField.toLowerCase();
        return !customColumns.some(col => {
          const dataIndex = col.dataIndex;
          if (typeof dataIndex === "string") {
            return dataIndex.toLowerCase() === fieldLower;
          }
          return false;
        });
      })
      .map(dateField => {
        const field = columnGridData.grid.find(
          (f: any) => f.field_name === dateField
        );
        return {
          title: field?.field_title ?? dateField,
          dataIndex: dateField.toLowerCase(),
          key: dateField,
          render: (_: any, record: any) => {
            const dateValue =
              record[dateField.toLowerCase()] ??
              record[dateField] ??
              record[dateField.toUpperCase()];
            return formatDate(dateValue);
          },
        };
      });

    return [
      ...customColumns,
      ...dateColumns,
      {
        title: "Active",
        dataIndex: "is_active",
        key: "is_active",
        width: 90,
        align: "center",
        render: (_: any, record: any) => {
          const recordId =
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? "";
          const pkStr = recordId ? String(recordId) : "";
          const isActive =
            record?.is_active === true ||
            record?.is_active === "true" ||
            record?.Is_Active === true;
          const isUpdating =
            updateActiveMutation.isPending &&
            updateActiveMutation.variables?.pk === pkStr;

          return (
            <Switch
              checked={!!isActive}
              disabled={!pkStr || isUpdating}
              onCheckedChange={checked => {
                if (!pkStr) {
                  return;
                }
                updateActiveMutation.mutate({
                  pk: pkStr,
                  is_active: !!checked,
                });
              }}
            />
          );
        },
      },
      {
        title: "Actions",
        width: 80,
        valueType: "option",
        key: "actions",
        fixed: "right",
        render: (_: any, record: any) => {
          const recordId =
            record?.id ?? record?.ID ?? record?.Id ?? record?._id;

          const handleViewTasks = () => {
            setViewTasksProjectId(String(recordId));
            const projectName =
              record?.projectname ??
              record?.project_name ??
              record?.projectName ??
              record?.name ??
              "";
            setViewTasksProjectName(String(projectName));
            setViewTasksModalOpen(true);
          };

          const handleNewTask = () => {
            navigate(`/tasks?project=${String(recordId)}&new=1`);
          };

          const handleSaveAsTemplate = async () => {
            if (!recordId) {
              notify.error("Project ID is missing");
              return;
            }
            try {
              const result = await PtsProjectService.saveProjectAsTemplate(
                String(recordId)
              );
              // API success: result is defined
              if (result !== undefined) {
                notify.success("Project saved as template successfully");
                return;
              }
              // API failed: axios get() returns undefined on error (does not throw)
              // notify.error("Failed to save project as template");
              setSaveAsTemplateProjectId(String(recordId));
              setSaveAsTemplateDialogOpen(true);
            } catch {
              // If it ever throws, also open the dialog
              // const errorMessage =
              //   error?.response?.data?.message ??
              //   error?.response?.data?.detail ??
              //   error?.response?.data?.error ??
              //   error?.message ??
              //   "Failed to save project as template";
              // notify.error(errorMessage);
              setSaveAsTemplateProjectId(String(recordId));
              setSaveAsTemplateDialogOpen(true);
            }
          };

          const menuItems: MenuProps["items"] = [
            {
              key: "view-tasks",
              label: "View tasks",
              icon: <ListTodo className="h-4 w-4" />,
              onClick: handleViewTasks,
            },
            {
              key: "new-task",
              label: "New task",
              icon: <FilePlus className="h-4 w-4" />,
              onClick: handleNewTask,
            },
            { type: "divider" },
            {
              key: "Save as Template",
              label: "Save as Template",
              icon: <Save className="h-4 w-4" />,
              onClick: handleSaveAsTemplate,
            },
            { type: "divider" },
            {
              key: "delete",
              label: "Delete",
              danger: true,
              icon: <Trash className="h-4 w-4" />,
              onClick: () => {
                Modal.confirm({
                  title: "Delete project",
                  content:
                    "Are you sure you want to delete this project? This action cannot be undone.",
                  okText: "Yes",
                  cancelText: "No",
                  okButtonProps: { danger: true },
                  onOk: () => handleDelete(recordId),
                });
              },
            },
          ];

          return (
            <Dropdown
              menu={{ items: menuItems }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <CustomButton
                type="text"
                icon={<MoreVertical size={18} />}
                title="Actions"
                onClick={e => e.stopPropagation()}
              />
            </Dropdown>
          );
        },
      },
    ];
  }, [
    columnGridData,
    queryClient,
    navigate,
    getEmployeeNamesById,
    formatDate,
    formatDateTime,
    updateActiveMutation,
  ]);

  const handleAddProject = () => {
    navigate(PTSNavigationRoutes.CREATE_PROJECT);
  };

  const toolBarRender = () => {
    return [
      <div
        key="toolbar-filters"
        className="flex w-full min-w-0 max-w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 md:justify-end"
      >
        <Tabs
          value={projectType}
          onValueChange={setProjectType}
          className="w-full min-w-0 sm:w-auto"
        >
          <TabsList className="h-auto w-full justify-stretch gap-1 p-1 sm:w-auto sm:justify-start">
            <TabsTrigger
              value="all"
              className="flex-1 px-2 text-xs data-[state=active]:border-b-0 sm:flex-none sm:px-3 sm:text-sm"
            >
              All
            </TabsTrigger>
            <TabsTrigger
              value="external"
              className="flex-1 px-2 text-xs data-[state=active]:border-b-0 sm:flex-none sm:px-3 sm:text-sm"
            >
              External
            </TabsTrigger>
            <TabsTrigger
              value="internal"
              className="flex-1 px-2 text-xs data-[state=active]:border-b-0 sm:flex-none sm:px-3 sm:text-sm"
            >
              Internal
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full min-w-0 sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by project name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10"
          />
        </div>
      </div>,
    ];
  };

  // Build filter object based on search query and project type
  const tableFilter = useMemo(() => {
    const filter: any = {
      is_pts_project: true,
    };

    if (searchQuery) {
      filter.projectname__ilike = `%${searchQuery}%`;
    }

    if (projectType !== "all") {
      filter.project_type1 = projectType;
    }

    return filter;
  }, [searchQuery, projectType]);

  // Filter for View tasks modal: tasks by project
  const viewTasksFilter = useMemo(
    () =>
      viewTasksProjectId
        ? { project__eq: viewTasksProjectId, is_pts_tsk: true }
        : undefined,
    [viewTasksProjectId]
  );

  // Fetch tasks for View tasks modal (card design)
  const { data: viewTasksData, isLoading: viewTasksLoading } = useQuery({
    queryKey: ["project-tasks", viewTasksProjectId, viewTasksFilter],
    queryFn: () =>
      DynamicModuleService.getItemsByPage(
        TASK_SCHEMA_NAME,
        1,
        100,
        viewTasksFilter ?? {}
      ),
    enabled: !!viewTasksProjectId && !!viewTasksFilter && viewTasksModalOpen,
  });

  const viewTasksList = useMemo(() => {
    if (!viewTasksData) {
      return [];
    }
    if (Array.isArray(viewTasksData)) {
      return viewTasksData;
    }
    const results = viewTasksData.results ?? viewTasksData.data ?? [];
    return Array.isArray(results) ? results : [];
  }, [viewTasksData]);

  // Fetch task category dropdown to resolve taskcategory ID to label (for View tasks modal)
  const { data: taskCategoryOptions } = useQuery({
    queryKey: ["task-category-dropdown", TASK_CATEGORY_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(TASK_CATEGORY_SCHEMA_NAME),
    enabled: viewTasksModalOpen && !!TASK_CATEGORY_SCHEMA_NAME,
  });

  const categoryIdToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    const options = Array.isArray(taskCategoryOptions)
      ? taskCategoryOptions
      : (taskCategoryOptions?.results ?? taskCategoryOptions?.data ?? []);
    (
      options as {
        value?: string;
        label?: string;
        id?: string;
        name?: string;
      }[]
    ).forEach((opt: Record<string, unknown>) => {
      const id = String(opt.value ?? opt.id ?? "");
      if (id) {
        map[id] = String(opt.label ?? opt.name ?? id);
      }
    });
    return map;
  }, [taskCategoryOptions]);

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Projects
          </h1>
          <p className="text-sm text-[#627384]">
            Manage project information and records
          </p>
        </div>
        {/* Add New Project Button */}
        <Button
          onClick={handleAddProject}
          className="bg-primary hover:bg-primary/90 flex items-center gap-2 whitespace-nowrap text-white"
        >
          <Plus className="h-4 w-4" />
          Add New Project
        </Button>
      </div>

      {/* Project Table */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <div className="pts-responsive-table-toolbar min-w-0">
            <ConfigDynamicTable
              tableName={PROJECT_SCHEMA_NAME}
              columns={columns}
              headerTitle="Project List"
              rowKey="id"
              filter={tableFilter}
              toolBarRender={toolBarRender}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={setSelectedRowKeys}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save as Template confirmation dialog (reference design: centered, icon, same colors) */}
      <Dialog
        open={saveAsTemplateDialogOpen}
        onOpenChange={open => {
          if (!saveAsTemplateConfirmLoading) {
            setSaveAsTemplateDialogOpen(open);
            if (!open) {
              setSaveAsTemplateProjectId(null);
            }
          }
        }}
      >
        <DialogContent className="rounded-lg border-0 bg-white p-8 text-center shadow-xl sm:max-w-[425px] [&>button]:text-[#6B7280] [&>button]:hover:text-[#374151]">
          <DialogHeader className="space-y-4 text-center sm:text-center">
            {/* Circular question mark icon - ref: light blue-gray bg, dark blue-gray outline & ? */}
            <div className="flex justify-center">
              <div className="border-primary flex h-14 w-14 items-center justify-center rounded-full border-2 bg-[#F0F2F5]">
                <HelpCircle className="text-primary h-7 w-7" strokeWidth={2} />
              </div>
            </div>
            <DialogTitle className="text-xl font-bold text-[#1F2937]">
              Save as template?
            </DialogTitle>
            <DialogDescription className="text-center text-sm font-normal leading-relaxed text-[#4B5563]">
              Are you sure you want to{" "}
              <span className="font-semibold text-[#4B5563]">
                save this project as a template
              </span>
              ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex flex-row justify-center gap-3 sm:justify-center">
            <Button
              type="button"
              variant="outline"
              disabled={saveAsTemplateConfirmLoading}
              onClick={() => {
                setSaveAsTemplateDialogOpen(false);
                setSaveAsTemplateProjectId(null);
              }}
              className="rounded-lg border-[#D1D5DB] bg-white px-5 py-2 text-[#4B5563] hover:border-[#D1D5DB] hover:bg-gray-50 hover:text-[#374151]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={saveAsTemplateConfirmLoading}
              onClick={async () => {
                if (!saveAsTemplateProjectId) {
                  return;
                }
                setSaveAsTemplateConfirmLoading(true);
                try {
                  const forcedResult =
                    await PtsProjectService.saveProjectAsTemplate(
                      saveAsTemplateProjectId,
                      true
                    );
                  if (forcedResult !== undefined) {
                    notify.success("Project saved as template successfully");
                    setSaveAsTemplateDialogOpen(false);
                    setSaveAsTemplateProjectId(null);
                  }
                } catch (forceError: any) {
                  const forceErrorMessage =
                    forceError?.response?.data?.message ??
                    forceError?.response?.data?.detail ??
                    forceError?.response?.data?.error ??
                    forceError?.message ??
                    "Failed to save project as template";
                  notify.error(forceErrorMessage);
                } finally {
                  setSaveAsTemplateConfirmLoading(false);
                }
              }}
              className="bg-primary hover:bg-primary/90 rounded-lg border-0 px-5 py-2 text-white"
            >
              {saveAsTemplateConfirmLoading
                ? "Saving..."
                : "Yes, save as template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View tasks modal: task list design matching image (category pill, name, hours; subtasks in boxes) */}
      <Modal
        title={
          viewTasksProjectName ? (
            <span className="text-lg font-bold">
              Tasks -{" "}
              <span className="text-[15px] font-medium text-gray-500">
                {viewTasksProjectName}
              </span>
            </span>
          ) : (
            "Tasks"
          )
        }
        open={viewTasksModalOpen}
        onCancel={() => {
          setViewTasksModalOpen(false);
          setViewTasksProjectId(null);
          setViewTasksProjectName("");
        }}
        footer={null}
        width={620}
        destroyOnClose
      >
        {viewTasksLoading ? (
          <div className="flex min-h-[200px] items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-2">
            {viewTasksList.length === 0 ? (
              <p className="py-8 text-center text-gray-500">
                No tasks for this project.
              </p>
            ) : (
              viewTasksList.map((task: any) => {
                const subtasks = Array.isArray(task.subtask)
                  ? task.subtask
                  : [];

                const renderTaskCard = (t: any, isSubtask: boolean) => {
                  const name = t.taskname ?? t.task_name ?? t.name ?? "-";
                  const catId = t.taskcategory ?? t.category ?? t.task_category;
                  const catLabel =
                    catId && categoryIdToLabel[String(catId)]
                      ? categoryIdToLabel[String(catId)]
                      : "—";
                  const hoursRaw =
                    t.taskestimatedhours ??
                    t.est_hours ??
                    t.estimated_hours ??
                    t.hours;
                  const hours =
                    hoursRaw !== null && hoursRaw !== ""
                      ? String(hoursRaw)
                      : "0";
                  return (
                    <div
                      key={t.id ?? t.ID ?? t._id ?? Math.random()}
                      className={
                        isSubtask
                          ? "flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-2.5 shadow-sm"
                          : "border-primary/30 mt-5 flex items-center justify-between gap-4 rounded-lg border bg-gray-100/90 px-4 py-3"
                      }
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <span className="truncate font-semibold text-gray-900">
                          {name}
                        </span>
                        <span className="inline-flex shrink-0 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                          {catLabel}
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="text-sm text-gray-500">{hours}h</span>
                      </div>
                    </div>
                  );
                };

                return (
                  <div key={task.id ?? task.ID ?? task._id ?? Math.random()}>
                    {renderTaskCard(task, false)}
                    {subtasks.length > 0 && (
                      <div className="ml-4 mt-2 pl-3">
                        <div className="space-y-2">
                          {subtasks.map((sub: any) =>
                            renderTaskCard(sub, true)
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProjectPage;
